import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Puzzle } from 'lucide-react'
import { Tooltip } from 'components/ds2/Tooltip'
import { Avatar } from 'components/ds2/Avatar'
import { Person } from './icons'
import { CHROME_STORE_URL } from 'pages/onboarding/utils'

interface ProfileFooterItemProps {
    isOpen: boolean
    name: string
    email: string
    onInstallApp?: () => void
    onHelp: () => void
    onLogout: () => void
}

type PopoverCoords = { placement: 'above'; bottom: number; left: number; minWidth: number } | { placement: 'right'; bottom: number; left: number }

export const ProfileFooterItem = ({ isOpen, name, email, onInstallApp, onHelp, onLogout }: ProfileFooterItemProps) => {
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [coords, setCoords] = useState<PopoverCoords | null>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!popoverOpen) return
        const handleOutside = (e: MouseEvent) => {
            if (triggerRef.current && !triggerRef.current.contains(e.target as Node) && popoverRef.current && !popoverRef.current.contains(e.target as Node))
                setPopoverOpen(false)
        }
        document.addEventListener('mousedown', handleOutside)
        return () => document.removeEventListener('mousedown', handleOutside)
    }, [popoverOpen])

    const handleTrigger = () => {
        if (!triggerRef.current) {
            setPopoverOpen((p) => !p)
            return
        }
        if (!popoverOpen) {
            const rect = triggerRef.current.getBoundingClientRect()
            if (isOpen) {
                setCoords({ placement: 'above', bottom: window.innerHeight - rect.top + 8, left: rect.left, minWidth: rect.width })
            } else {
                setCoords({ placement: 'right', bottom: window.innerHeight - rect.bottom, left: rect.right + 8 })
            }
        }
        setPopoverOpen((p) => !p)
    }

    const avatar = <Avatar variant="iconSoft" type="accent" icon={<Person />} size={32} />

    const triggerEl = (
        <button
            ref={triggerRef}
            type="button"
            onClick={handleTrigger}
            className={[
                'flex w-full items-center gap-2 rounded-xl transition-colors',
                isOpen ? 'px-2 py-1.5' : 'h-9 justify-center',
                popoverOpen ? 'bg-accent-soft' : 'hover:bg-default',
            ].join(' ')}
        >
            {avatar}
            {isOpen && (
                <span className="flex min-w-0 flex-1 flex-col text-left">
                    <span className="truncate text-sm font-semibold text-foreground">{name || 'Numeo'}</span>
                    <span className="truncate text-[11px] text-foreground-muted">{email}</span>
                </span>
            )}
        </button>
    )

    const popoverStyle: React.CSSProperties = !coords
        ? {}
        : coords.placement === 'above'
          ? { position: 'fixed', bottom: coords.bottom, left: coords.left, minWidth: coords.minWidth, zIndex: 50 }
          : { position: 'fixed', bottom: coords.bottom, left: coords.left, minWidth: 200, zIndex: 50 }

    return (
        <>
            {!isOpen ? (
                <Tooltip content={name || 'Profile'} placement="right">
                    {triggerEl}
                </Tooltip>
            ) : (
                triggerEl
            )}

            {popoverOpen &&
                coords &&
                createPortal(
                    <div ref={popoverRef} style={popoverStyle} className="rounded-2xl border border-border bg-surface p-1 shadow-overlay">
                        {/* Install Extension */}
                        <button
                            type="button"
                            onClick={() => {
                                window.open(CHROME_STORE_URL, '_blank', 'noopener,noreferrer')
                                setPopoverOpen(false)
                            }}
                            className="flex h-9 w-full items-center gap-2.5 rounded-xl px-3 text-sm font-medium text-foreground transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                        >
                            <Puzzle size={16} />
                            <span>Install Extension</span>
                        </button>

                        {/* Install App */}
                        <button
                            type="button"
                            onClick={() => {
                                if (onInstallApp) onInstallApp()
                                else window.open('https://numeo.ai/download', '_blank')
                                setPopoverOpen(false)
                            }}
                            className="flex h-9 w-full items-center gap-2.5 rounded-xl px-3 text-sm font-medium text-foreground transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                        >
                            <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path
                                    d="M8 2v8M5 7.5 8 10.5l3-3M3 13h10"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <span>Install App</span>
                        </button>

                        {/* Help center */}
                        <button
                            type="button"
                            onClick={() => {
                                onHelp()
                                setPopoverOpen(false)
                            }}
                            className="flex h-9 w-full items-center gap-2.5 rounded-xl px-3 text-sm font-medium text-foreground transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                        >
                            <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.3" />
                                <path
                                    d="M6.5 6.25a1.5 1.5 0 1 1 2.121 1.371C8.107 7.88 7.75 8.38 7.75 9.25"
                                    stroke="currentColor"
                                    strokeWidth="1.3"
                                    strokeLinecap="round"
                                />
                                <circle cx="7.75" cy="11.25" r="0.75" fill="currentColor" />
                            </svg>
                            <span className="flex-1 text-left">Help center</span>
                            <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden="true" className="text-foreground-muted">
                                <path d="M2 10 10 2M10 2H5.5M10 2v4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <div className="my-1 h-px bg-border" />

                        {/* Log out */}
                        <button
                            type="button"
                            onClick={() => {
                                onLogout()
                                setPopoverOpen(false)
                            }}
                            className="flex h-9 w-full items-center gap-2.5 rounded-xl px-3 text-sm font-medium text-danger transition-colors hover:bg-danger-soft"
                        >
                            <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path
                                    d="M6 14H3.5A1.5 1.5 0 0 1 2 12.5v-9A1.5 1.5 0 0 1 3.5 2H6"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path d="M10.5 11 14 8l-3.5-3.5M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>Log out</span>
                        </button>
                    </div>,
                    document.body
                )}
        </>
    )
}
